import { TranslateService } from '@ngx-translate/core';

export const STOCK_HOLD_FAILURE_REASON = 'STOCK_HOLD_TIMEOUT';
export const STOCK_HOLD_WARNING_MINUTES = 5;

export const STOCK_HOLD_CATEGORY_CODES = {
  COD_UNCONFIRMED: 'COD_UNCONFIRMED',
  PREPAID: 'PREPAID',
  MARKETPLACE_COD: 'MARKETPLACE_COD',
  NO_TIMEOUT: 'NO_TIMEOUT',
} as const;

export type StockHoldCategoryCode =
  (typeof STOCK_HOLD_CATEGORY_CODES)[keyof typeof STOCK_HOLD_CATEGORY_CODES];

export const CONFIGURABLE_STOCK_HOLD_CATEGORIES = [
  STOCK_HOLD_CATEGORY_CODES.COD_UNCONFIRMED,
  STOCK_HOLD_CATEGORY_CODES.PREPAID,
  STOCK_HOLD_CATEGORY_CODES.MARKETPLACE_COD,
] as const;

export function compareStockHoldCategoryOrder(
  categoryA: string,
  categoryB: string,
): number {
  const order = CONFIGURABLE_STOCK_HOLD_CATEGORIES as readonly string[];
  const rankA = order.indexOf(categoryA);
  const rankB = order.indexOf(categoryB);
  return (
    (rankA === -1 ? Number.MAX_SAFE_INTEGER : rankA) -
    (rankB === -1 ? Number.MAX_SAFE_INTEGER : rankB)
  );
}

export function getStockHoldCategoryLabelKey(category?: string | null): string {
  if (!category) {
    return 'stock_hold_timeout.order.category.unknown';
  }
  switch (category.toUpperCase()) {
    case STOCK_HOLD_CATEGORY_CODES.COD_UNCONFIRMED:
      return 'stock_hold_timeout.order.category.cod_unconfirmed';
    case STOCK_HOLD_CATEGORY_CODES.PREPAID:
      return 'stock_hold_timeout.order.category.prepaid';
    case STOCK_HOLD_CATEGORY_CODES.MARKETPLACE_COD:
      return 'stock_hold_timeout.order.category.marketplace_cod';
    case STOCK_HOLD_CATEGORY_CODES.NO_TIMEOUT:
      return 'stock_hold_timeout.order.category.no_timeout';
    default:
      return 'stock_hold_timeout.order.category.unknown';
  }
}

export function formatTimeoutMinutes(
  minutes: number | null | undefined,
  translate: TranslateService,
): string {
  if (minutes == null || minutes <= 0) {
    return translate.instant('stock_hold_timeout.order.no_limit');
  }
  if (minutes % (24 * 60) === 0) {
    const hours = minutes / 60;
    return translate.instant('stock_hold_timeout.order.timeout_hours', {
      hours: hours,
    });
  }
  if (minutes % 60 === 0) {
    return translate.instant('stock_hold_timeout.order.timeout_hours', {
      hours: minutes / 60,
    });
  }
  return translate.instant('stock_hold_timeout.order.timeout_minutes', {
    minutes,
  });
}

export function isStockHoldExpiringSoon(
  expiryTime?: string | Date | null,
  warningMinutes: number = STOCK_HOLD_WARNING_MINUTES,
): boolean {
  if (!expiryTime) {
    return false;
  }
  const expiryMs = new Date(expiryTime).getTime();
  if (Number.isNaN(expiryMs)) {
    return false;
  }
  const remainingMs = expiryMs - Date.now();
  return remainingMs > 0 && remainingMs <= warningMinutes * 60 * 1000;
}

export function isStockHoldExpired(expiryTime?: string | Date | null): boolean {
  if (!expiryTime) {
    return false;
  }
  const expiryMs = new Date(expiryTime).getTime();
  return !Number.isNaN(expiryMs) && expiryMs <= Date.now();
}

export function isStockHoldTimeoutOrder(
  failureReasonDescription?: string | null,
): boolean {
  return failureReasonDescription === STOCK_HOLD_FAILURE_REASON;
}

/** True when expiry should show as expired (past time or cancelled by timeout). */
export function isStockHoldExpiryExpired(
  expiryTime?: string | Date | null,
  failureReasonDescription?: string | null,
): boolean {
  return (
    isStockHoldTimeoutOrder(failureReasonDescription) ||
    isStockHoldExpired(expiryTime)
  );
}

/** True when an expiry value/badge should render (has time, or timeout-cancelled). */
export function hasStockHoldExpiryDisplay(
  expiryTime?: string | Date | null,
  failureReasonDescription?: string | null,
): boolean {
  return (
    !!expiryTime || isStockHoldTimeoutOrder(failureReasonDescription)
  );
}
